import { forwardRef } from 'react';
import styled from 'styled-components';
import { color, space, typography } from 'styled-system';
import { HTMLKaajaProps } from '../../types';
import { Stack } from '../stack';

type RadioProps = HTMLKaajaProps<'input'> & {
  label?: string;
  hint?: string;
  variant?: 'default' | 'error' | 'warning';
};

const Input = styled('input')<RadioProps>((props) => ({
  appearance: 'none',
  position: 'relative',

  width: '20px',
  height: '20px',
  display: 'inline-block',
  outlineOffset: '3px',
  cursor: 'pointer',
  borderRadius: '50%',
  borderWidth: '3px',
  borderStyle: 'solid',
  borderColor: 'white',
  background: 'neutral50',

  ...(props.variant === 'default' && {
    boxShadow: `0 0 0 2px ${props.theme.colors.blueLight700}`,

    ':checked': {
      background: props.theme.colors.blueLight700,
      boxShadow: `0 0 0 2px ${props.theme.colors.blueLight700}`,
    },

    ':focus-visible': {
      outlineOffset: 0,
    },
  }),

  ...(props.variant === 'warning' && {
    boxShadow: `0 0 0 2px ${props.theme.colors.feedbackWarning}`,

    ':checked': {
      background: props.theme.colors.feedbackWarning,
      boxShadow: `0 0 0 2px ${props.theme.colors.feedbackWarning}`,
    },

    ':focus-visible': {
      outlineOffset: 0,
    },
  }),

  ...(props.variant === 'error' && {
    boxShadow: `0 0 0 2px ${props.theme.colors.feedbackError}`,

    ':checked': {
      background: props.theme.colors.feedbackError,
      boxShadow: `0 0 0 2px ${props.theme.colors.feedbackError}`,
    },

    ':focus-visible': {
      outlineOffset: 0,
    },
  }),

  ...(props.disabled === true && {
    boxShadow: `0 0 0 2px ${props.theme.colors.neutral500}`,

    ':checked': {
      background: props.theme.colors.neutral500,
      boxShadow: `0 0 0 2px ${props.theme.colors.neutral500}`,
    },

    ':focus-visible': {
      outlineOffset: 0,
    },
  }),
}));

const Label = styled('label')(
  (props) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '12px',
    fontSize: props.theme.fontSizes[1],
    fontWeight: 400,
  }),
  space,
  color,
  typography
);

const Hint = styled('div')((props) => ({
  color: props.theme.colors.neutral500,
  fontSize: props.theme.fontSizes[1],
}));

const Radio = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const { type = 'radio', variant = 'default', label, hint, ...rest } = props;

  return (
    <Stack flexDirection="column" gap={3}>
      <Label>
        <Input ref={ref} variant={variant} type={type} {...rest} />
        {label}
      </Label>
      {hint && <Hint>{hint}</Hint>}
    </Stack>
  );
});

export { Radio };
